import React from 'react';
import styled from '@benzinga/themetron';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { Icon, Span } from '@benzinga/core-ui';

export interface LiveVideoCardProps {
  id: string;
  title: string;
  subtitle?: string;
}

export const LiveVideoCard: React.FC<LiveVideoCardProps> = ({ id, subtitle = '', title }) => {
  return (
    <LiveVideoCardContainer
      backgroundImage={`https://i.ytimg.com/vi/${id}/hq720.jpg`}
      className="live-video-card-container"
    >
      <a href={`https://www.youtube.com/watch?v=${id}`} rel="noopener noreferrer" target="_blank">
        <div className="background-div">
          <div className="gradient-overlay"></div>
          <div className="video-info-container">
            <div className="on-air-badge">On Air</div>
            <h2 className="title">
              <Span lines={3}>{title}</Span>
            </h2>
            <p className="subtitle">{subtitle}</p>
            <div className="play-container">
              <div className="play-icon-wrapper">
                <Icon icon={faPlay} />
              </div>
              <div className="play-now-text">Play Now</div>
            </div>
          </div>
        </div>
      </a>
    </LiveVideoCardContainer>
  );
};

const LiveVideoCardContainer = styled.div<{ backgroundImage: string }>`
  &.live-video-card-container {
    height: 100%;

    .background-div {
      position: relative;
      height: 100%;
      width: 100%;
      background-blend-mode: difference;
      background-color: rgb(23, 37, 58);
      background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
      background-position: right center;
      background-repeat: no-repeat;
      background-size: contain;
      border-radius: 4px;
      display: inline-block;
      width: 100%;
    }

    .gradient-overlay {
      position: absolute;
      height: 100%;
      width: 100%;
      z-index: 2;
      mix-blend-mode: normal;
      background: linear-gradient(to right, rgb(23, 37, 58, 0.98) 20%, rgba(0, 39, 74, 0.67) 100%);
      filter: blur(2px);
    }

    .video-info-container {
      position: absolute;
      height: 100%;
      width: 100%;
      max-width: 40rem;
      z-index: 3;
      color: ${({ theme }) => theme.colorPalette.white};
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      top: 0;
      padding: 1rem;

      @media screen and (min-width: 550px) {
        padding: 3rem;
      }
    }

    .on-air-badge {
      font-size: ${({ theme }) => theme.fontSize.sm};
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      padding: 0.5rem 1.5rem;
      border-radius: ${({ theme }) => theme.borderRadius.default};
      text-transform: uppercase;
      background-color: ${({ theme }) => theme.colorPalette.red600};
      position: absolute;
      top: 40px;
      @media screen and (max-width: 800px) {
        top: 10px;
        padding: 0.25rem 1rem;
      }
    }

    .title {
      margin-top: 0.25rem;
      font-size: ${({ theme }) => theme.fontSize['4xl']};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      text-transform: uppercase;
      color: ${({ theme }) => theme.colorPalette.white};
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      @media screen and (max-width: 800px) {
        font-size: ${({ theme }) => theme.fontSize['xl']};
      }
    }

    .subtitle {
      color: ${({ theme }) => theme.colorPalette.gray300};
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      margin-top: 20px;
    }

    .play-container {
      display: flex;
      align-items: center;
      position: absolute;
      bottom: 40px;
      @media screen and (max-width: 800px) {
        bottom: 10px;
      }
    }

    .play-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: ${({ theme }) => theme.borderRadius.full};
      height: 2.5rem;
      width: 2.5rem;
      bottom: 1rem;
      background-color: ${({ theme }) => theme.colorPalette.blue500};
      box-shadow: 0px 4px 28px #1979ff;

      > span {
        color: ${({ theme }) => theme.colorPalette.white};
        font-size: ${({ theme }) => theme.fontSize.xs};
      }
    }

    .play-now-text {
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      margin-left: 0.5rem;
      text-transform: uppercase;
    }
  }
`;
